import {
  Box,
  Button,
  createStyles,
  makeStyles,
  Paper,
  TextField,
  Theme,
  Typography,
  Hidden,
  Chip,
  Grid,
  Tooltip,
} from '@material-ui/core';
import AttachmentIcon from '@material-ui/icons/Attachment';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import React, { useRef, useState, useEffect } from 'react';
import NumberFormat from 'react-number-format';
import ClearIcon from '@material-ui/icons/Clear';
import ErrorIcon from '@material-ui/icons/Error';

import LoadingMask from './LoadingMask';
import { UpsertPaymentRequestStatus } from '../gql-types.generated';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      textAlign: 'start',
      position: 'relative',
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
      },
    },
    contentBox: {
      padding: theme.spacing(2, 3, 3, 3),
    },
    header: {
      padding: theme.spacing(3, 3, 1, 3),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerText: {
      height: '100%',
      maxWidth: '85%',
    },
    headerHeadText: {
      letterSpacing: 1.5,
      fontSize: 12,
    },
    headerIcon: {
      lineHeight: 1,
      borderRadius: 50,
      backgroundColor: theme.palette.grey[200],
      padding: 8,
    },
    headerSuccessIcon: {
      lineHeight: 1,
      paddingRight: 8,
    },
    headerErrorIcon: {
      lineHeight: 1,
      paddingRight: 8,
    },
    errorHeader: {
      padding: theme.spacing(1, 3),
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#ffe7e6',
    },
    successHeader: {
      padding: theme.spacing(1, 3),
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#EAF9EE',
    },
    settingsErrorHeader: {
      padding: theme.spacing(1.5, 3),
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#ffe7e6',
    },
    settingsErrorHeaderText: {
      height: '100%',
      maxWidth: '85%',
      color: theme.palette.error.main,
    },
    errorHeaderText: {
      height: '100%',
      maxWidth: '85%',
      color: theme.palette.error.main,
    },
    errorHeaderTypography: {
      fontWeight: 500,
    },
    successHeaderText: {
      height: '100%',
      maxWidth: '85%',
      color: 'green',
    },
    icon: {
      fontSize: '2rem',
      color: theme.palette.text.secondary,
    },
    settingErrorIcon: {
      fontSize: '1.5rem',
      color: theme.palette.error.main,
    },
    errorIcon: {
      fontSize: '2rem',
      color: theme.palette.error.main,
    },
    successIcon: {
      fontSize: '2rem',
      color: 'green',
    },
    hidden: {
      display: 'none',
    },
    textField: {
      '&.selectEmptyMissing .MuiOutlinedInput-notchedOutline': {
        border: '1px solid',
        borderColor: theme.palette.error.main,
      },
      '&.selectEmptyMissing label': {
        color: theme.palette.error.main,
      },
    },
    hiddenFileField: {
      display: 'none',
    },
    fileButton: {
      '&.selectEmptyMissing': {
        border: '1px solid red',
      },
      minHeight: '36px',
      justifyContent: 'center',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    fileButtonText: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      fontSize: '14px',
      fontWeight: 500,
      letterSpacing: '0.39998px',
    },
    fileChip: {
      fontSize: '16px',
      padding: '6px',
      height: 'auto',
      borderRadius: '4px',
      justifyContent: 'space-between',
      maxWidth: '100%',
    },
    radios: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 4,
      paddingBottom: 4,
    },
    closeButton: {
      color: 'black',
    },
    sendButton: {
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
      [theme.breakpoints.up('xs')]: {
        minWidth: 100,
      },
    },
    headerPaymentBanner: {
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        cursor: 'pointer',
        textDecoration: 'underline',
      },
    },
    tooltip: {
      fontSize: '10pt',
      textAlign: 'center',
      verticalAlign: 'middle',
    },
  }),
);

interface StateObj {
  successRecipient: string;
  fileChipStyle: string;
  fileButtonStyle: string;
}

interface RequestPaymentProps {
  defaultCurrency?: string | null;
  requestPaymentUploadFile: (file: File) => void;
  requestPaymentUpsertPaymentRequest: (
    referenceId: string,
    amount: number,
    invoiceRef: string,
    emailAddress?: string,
    phoneNumber?: string,
  ) => void;
  requestPaymentClearPaymentRequestStatus: () => void;
  invoiceStorageId: string | undefined;
  paymentRequestStatus?: UpsertPaymentRequestStatus;
  paymentsDisabled: boolean;
  clearDocument: () => void;
  handleSettingsClick: () => void;
  refreshTransactionData: () => void;
  sendPaymentRequestError: string | undefined;
  handlePaymentRequestFormValid: (arg1: boolean) => void;
  isPaymentRequestFormValid: boolean;
  handleRecipientEmailValid: (arg1: boolean) => void;
  isRecipientEmailValid: boolean;
  handleRecipientPhoneValid: (arg1: boolean) => void;
  isRecipientPhoneValid: boolean;
  handleAmountValid: (arg1: boolean) => void;
  isAmountValid: boolean;
  handleRefNumValid: (arg1: boolean) => void;
  isRefNumValid: boolean;
}

interface NumberFormatCustomProps {
  inputRef: (instance: NumberFormat | null) => void;
  onChange: (event: { target: { value: string } }) => void;
}

function NumberFormatCustom(props: NumberFormatCustomProps) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={values => {
        onChange({
          target: {
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
    />
  );
}

function MaskFormatCustom(props: NumberFormatCustomProps) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={values => {
        onChange({
          target: {
            value: values.value,
          },
        });
      }}
      format="(###) ###-####"
    />
  );
}

interface FileOptions {
  file: File | undefined;
  buttonText: string;
  buttonFrontIcon: JSX.Element | undefined;
  buttonEndIcon: JSX.Element | undefined;
  fileSelected: boolean;
}

const fileMaxSize = 10000000;
const SendPaymentRequest: React.FC<RequestPaymentProps> = (props: RequestPaymentProps) => {
  const classes = useStyles();
  const {
    paymentRequestStatus,
    invoiceStorageId,
    defaultCurrency,
    clearDocument,
    sendPaymentRequestError,
    isPaymentRequestFormValid,
    handlePaymentRequestFormValid,
    isRecipientEmailValid,
    handleRecipientEmailValid,
    isRecipientPhoneValid,
    handleRecipientPhoneValid,
    isAmountValid,
    handleAmountValid,
    isRefNumValid,
    handleRefNumValid,
  } = props;
  const currencyType = defaultCurrency || 'USD';
  const [screenState, setScreenState] = useState<StateObj>({
    successRecipient: '',
    fileButtonStyle: classes.fileButton,
    fileChipStyle: classes.hidden,
  });
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [refNum, setRefNum] = useState('');
  const [requestState, setRequestState] = useState('');
  const [fileOptions, setFileOptions] = useState<FileOptions>({
    file: undefined,
    buttonText: 'Upload Invoice',
    buttonFrontIcon: <AttachmentIcon />,
    buttonEndIcon: undefined,
    fileSelected: false,
  });
  const [loading, setLoading] = useState(false);
  const emailRef = useRef<HTMLDivElement>(null);
  const smsRef = useRef<HTMLDivElement>(null);
  const amountRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const refRef = useRef<HTMLDivElement>(null);
  const fileUploadButtonRef = useRef<HTMLButtonElement>(null);

  const appInsightsContext = useAppInsightsContext();
  const trackPaymentRequest = useTrackEvent(appInsightsContext, 'Sending Payment Request', {});
  const trackPaymentRequestWithMobile = useTrackEvent(appInsightsContext, 'Payment Request With Mobile Number', {});

  const handleFileUploadError = () => {
    setFileOptions({
      file: undefined,
      buttonText: 'Upload Invoice',
      buttonFrontIcon: <AttachmentIcon />,
      buttonEndIcon: undefined,
      fileSelected: false,
    });
    setRequestState('uploadError');
    setLoading(false);
  };

  if (loading) {
    if (props.invoiceStorageId) {
      if (props.invoiceStorageId !== 'uploadError') {
        if (fileOptions.file && fileOptions.buttonText !== 'Upload Invoice') {
          setLoading(false);
        }
      } else {
        handleFileUploadError();
      }
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event.target) {
      const input = event.target;
      if (input.files && input.files[0]) {
        const file = input.files[0];
        if (file.size <= fileMaxSize) {
          setLoading(true);
          setFileOptions({
            file,
            buttonText: file.name,
            buttonFrontIcon: undefined,
            buttonEndIcon: <CloseIcon className={classes.closeButton} />,
            fileSelected: true,
          });
          setScreenState({
            successRecipient: recipientEmail,
            fileButtonStyle: classes.hidden,
            fileChipStyle: classes.fileChip,
          });
          setRequestState('');
          if (refRef && refRef.current) {
            refRef.current.classList.remove('selectEmptyMissing');
          }
          props.requestPaymentUploadFile(file);
        } else {
          setRequestState('fileSize');
        }
      }
    }
  };

  const clearFileInput = () => {
    if (fileRef && fileRef.current) {
      // remove file
      fileRef.current.value = '';
      console.log('actual clear file');
      setFileOptions({
        file: undefined,
        buttonText: 'Upload Invoice',
        buttonFrontIcon: <AttachmentIcon />,
        buttonEndIcon: undefined,
        fileSelected: false,
      });
      setScreenState({
        successRecipient: recipientEmail,
        fileButtonStyle: classes.fileButton,
        fileChipStyle: classes.hidden,
      });
    }
    clearDocument();
    setRequestState('');
    setLoading(false);
  };

  const handleUploadClick = () => {
    if (!fileOptions.file) {
      // set hidden field focus
      setTimeout(() => {
        if (fileRef && fileRef.current) {
          fileRef.current.click();
        }
      }, 100);

      setScreenState({
        successRecipient: recipientEmail,
        fileButtonStyle: classes.fileButton,
        fileChipStyle: classes.hidden,
      });
    }
  };

  const validateEmail = (value: string) => {
    if (!emailRef || !emailRef.current) {
      return false;
    }

    const emailFormat = RegExp(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/);
    const testPassed = emailFormat.test(value);
    if (!testPassed || value.length === 0) {
      return false;
    }

    return true;
  };

  const validatePhone = (value: string) => {
    if (!smsRef || !smsRef.current) {
      return false;
    }

    if (value.length) {
      if (value.length !== 10) {
        return false;
      }
    }

    return true;
  };

  const validateReference = (value: string) => {
    if (!refRef || !refRef.current) {
      return false;
    }

    const refFormat = RegExp(/^[a-zA-Z0-9-]*$/);
    const refPassed = refFormat.test(value);
    if (value.length === 0 || !refPassed) {
      return false;
    }

    return true;
  };

  const validateAmount = (value: string) => {
    if (!amountRef || !amountRef.current) {
      return false;
    }

    if (!value || parseFloat(value) <= 0) {
      return false;
    }

    return true;
  };

  const addErrorToFileButton = () => {
    if (fileUploadButtonRef && fileUploadButtonRef.current) {
      fileUploadButtonRef.current.classList.add('selectEmptyMissing');
    }
  };

  const removeErrors = () => {
    if (fileUploadButtonRef && fileUploadButtonRef.current) {
      fileUploadButtonRef.current.classList.remove('selectEmptyMissing');
    }
    setRequestState('');
  };

  const handleRecipientEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setRecipientEmail(value);
    handleRecipientEmailValid(validateEmail(value));
  };

  const handleRecipientPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setRecipientPhone(value);
    handleRecipientPhoneValid(validatePhone(value));
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setAmount(value);
    handleAmountValid(validateAmount(value));
  };

  const handleRefNumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setRefNum(value);
    handleRefNumValid(validateReference(value));
  };

  const handleSendClick = () => {
    console.log('sendClicked: top');

    let errors = 0;
    removeErrors();

    if (fileOptions.file === undefined) {
      addErrorToFileButton();
      errors += 1;
    }

    if (errors === 0) {
      const submitAmount = Math.round(parseFloat(amount) * 100);
      if (invoiceStorageId && submitAmount) {
        trackPaymentRequest({});

        if (recipientPhone) {
          trackPaymentRequestWithMobile({});
        }
        props.requestPaymentUpsertPaymentRequest(refNum, submitAmount, invoiceStorageId, recipientEmail, recipientPhone);
        handlePaymentRequestFormValid(false);
      }
    } else {
      setRequestState('error');
    }
  };

  const handlePendingRequest = () => {
    // Show Success
    setRequestState('success');
    setScreenState({
      successRecipient: recipientPhone,
      fileButtonStyle: screenState.fileButtonStyle,
      fileChipStyle: screenState.fileChipStyle,
    });

    setRecipientPhone('');
    setRecipientEmail('');
    setAmount('');
    setRefNum('');
    clearFileInput();
    props.requestPaymentClearPaymentRequestStatus();
    handlePaymentRequestFormValid(false);
  };

  const validateFields = () => {
    if (
      validateEmail(recipientEmail) &&
      validatePhone(recipientPhone) &&
      validateAmount(amount) &&
      validateReference(refNum) &&
      fileOptions.fileSelected
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    handlePaymentRequestFormValid(validateFields());
  }, [recipientEmail, recipientPhone, amount, refNum, fileOptions]);

  useEffect(() => {
    if (paymentRequestStatus !== undefined) {
      handlePendingRequest();
      props.refreshTransactionData();
    }
    handleAmountValid(true);
  }, [paymentRequestStatus]);

  useEffect(() => {
    if (sendPaymentRequestError) {
      handlePaymentRequestFormValid(false);
    }
  }, [sendPaymentRequestError]);

  const generateHeader = () => {
    if (requestState !== '') {
      // if error show red
      let subtitle;
      if (requestState !== 'success') {
        if (requestState === 'error') {
          subtitle = 'Failed to send, Invalid values';
        }
        if (requestState === 'fileSize') {
          subtitle = 'File uploaded exceeds max size (10MB)';
        }
        if (requestState === 'uploadError') {
          subtitle = 'Document Upload Failure, check connection and try again';
        }
        if (subtitle) {
          return (
            <Box className={classes.errorHeader}>
              <Box className={classes.headerErrorIcon}>
                <SendIcon className={classes.errorIcon} />
              </Box>
              <Box className={classes.errorHeaderText} data-cy="payment-request-error">
                <Typography className={classes.errorHeaderTypography} variant="subtitle2">
                  {subtitle}
                </Typography>
              </Box>
            </Box>
          );
        }
      }
      // if submitted show green
      subtitle = `Payment Request Sent to ${screenState.successRecipient}`;
      return (
        <Box className={classes.successHeader}>
          <Box className={classes.headerSuccessIcon}>
            <SendIcon className={classes.successIcon} />
          </Box>
          <Box className={classes.successHeaderText}>
            <Typography variant="subtitle2">{subtitle}</Typography>
          </Box>
        </Box>
      );
    }
    if (props.paymentsDisabled) {
      return (
        <Box className={classes.settingsErrorHeader}>
          <Box className={classes.headerErrorIcon}>
            <ErrorIcon className={classes.settingErrorIcon} />
          </Box>
          <Box className={classes.settingsErrorHeaderText} data-cy="payment-request-error">
            <Typography className={classes.headerPaymentBanner} onClick={props.handleSettingsClick}>
              Please update payout settings
            </Typography>
          </Box>
        </Box>
      );
    }
    if (sendPaymentRequestError) {
      if (sendPaymentRequestError.includes('timeout')) {
        return (
          <Box className={classes.settingsErrorHeader}>
            <Box className={classes.headerErrorIcon}>
              <ErrorIcon className={classes.settingErrorIcon} />
            </Box>
            <Box className={classes.settingsErrorHeaderText} data-cy="payment-request-error">
              <Typography className={classes.errorHeaderTypography}>Timeout exceeded. Please try again</Typography>
            </Box>
          </Box>
        );
      }
      return (
        <Box className={classes.settingsErrorHeader}>
          <Box className={classes.headerErrorIcon}>
            <ErrorIcon className={classes.settingErrorIcon} />
          </Box>
          <Box className={classes.settingsErrorHeaderText} data-cy="payment-request-error">
            <Typography className={classes.errorHeaderTypography}>{sendPaymentRequestError}</Typography>
          </Box>
        </Box>
      );
    }
    // otherwise show regular
    return (
      <Box className={classes.header}>
        <Box className={classes.headerText}>
          <Typography variant="subtitle2" className={classes.headerHeadText}>
            PAYMENT REQUEST
          </Typography>
          <Typography variant="h6">Send a New Request</Typography>
        </Box>
        <Hidden mdDown>
          <Box className={classes.headerIcon}>
            <SendIcon className={classes.icon} />
          </Box>
        </Hidden>
      </Box>
    );
  };

  const header = generateHeader();
  return (
    <Paper className={classes.paper}>
      <LoadingMask loading={loading} />
      {header}
      <Box className={classes.contentBox}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="Email Address"
              aria-required="true"
              className={classes.textField}
              autoComplete="off"
              type="email"
              value={recipientEmail}
              error={!isRecipientEmailValid}
              helperText={isRecipientEmailValid ? null : 'Invalid Email Address'}
              onChange={handleRecipientEmailChange}
              ref={emailRef}
              disabled={props.paymentsDisabled}
              data-cy="recipient-email"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="Mobile Number (Optional)"
              className={classes.textField}
              aria-required="false"
              autoComplete="off"
              type="tel"
              value={recipientPhone}
              error={!isRecipientPhoneValid}
              helperText={isRecipientPhoneValid ? null : 'Invalid Phone Number'}
              onChange={handleRecipientPhoneChange}
              ref={smsRef}
              InputProps={{
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                inputComponent: MaskFormatCustom as any,
              }}
              disabled={props.paymentsDisabled}
              data-cy="recipient-phone"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label={`$ Amount (${currencyType})`}
              aria-required="true"
              className={classes.textField}
              error={!isAmountValid}
              value={amount}
              helperText={isAmountValid ? null : 'Invalid Amount'}
              onChange={handleAmountChange}
              InputProps={{
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                inputComponent: NumberFormatCustom as any,
              }}
              ref={amountRef}
              disabled={props.paymentsDisabled}
              data-cy="amount"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="Reference Number"
              aria-required="true"
              className={classes.textField}
              error={!isRefNumValid}
              value={refNum}
              helperText={isRefNumValid ? null : 'Invalid Reference Number'}
              onChange={handleRefNumChange}
              ref={refRef}
              disabled={props.paymentsDisabled}
              data-cy="reference-number"
            />
          </Grid>
          <TextField
            className={classes.hiddenFileField}
            onChange={handleFileChange}
            aria-required="true"
            type="file"
            inputProps={{
              accept: '.pdf',
            }}
            inputRef={fileRef}
            data-cy="invoice"
          />
        </Grid>
        <Grid container spacing={2} style={{ marginTop: '8px' }}>
          <Grid item xs={12} sm>
            <Button
              className={screenState.fileButtonStyle}
              variant="outlined"
              color="primary"
              title={fileOptions.buttonText}
              hidden={fileOptions.fileSelected}
              startIcon={fileOptions.buttonFrontIcon}
              endIcon={fileOptions.buttonEndIcon}
              onClick={handleUploadClick}
              ref={fileUploadButtonRef}
              disabled={props.paymentsDisabled}
              data-cy="upload-invoice"
              fullWidth={true}
            >
              <Typography className={classes.fileButtonText}>{fileOptions.buttonText}</Typography>
            </Button>
            <Tooltip
              title={<span className={classes.tooltip}>{fileOptions.buttonText}</span>}
              PopperProps={{
                popperOptions: {
                  modifiers: {
                    offset: {
                      enabled: true,
                      offset: '0px, -85px',
                    },
                  },
                },
              }}
            >
              <Chip
                label={fileOptions.buttonText}
                onDelete={clearFileInput}
                variant="outlined"
                hidden={!fileOptions.fileSelected}
                className={screenState.fileChipStyle}
                deleteIcon={<ClearIcon />}
                data-cy="delete-invoice"
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm>
            <Box textAlign="right">
              <Button
                className={classes.sendButton}
                variant="contained"
                disableElevation
                color="primary"
                onClick={handleSendClick}
                disabled={!isPaymentRequestFormValid}
                data-cy="send-payment"
                fullWidth={true}
              >
                SEND
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default SendPaymentRequest;
